import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

// Admin audit log (read-only): who did what, when. Records member edits/deletes,
// viewing user AI-chat transcripts, and (future) manual triggers.

const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

const ACTION_STYLE = {
  VIEW_CHAT_TRANSCRIPT: 'bg-blue-100 text-blue-700',
  EDIT_MEMBER: 'bg-amber-100 text-amber-700',
  DELETE_MEMBER: 'bg-red-100 text-red-700',
};

const AdminAuditLog = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/admin/audit', { params: { limit: 200 } });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return [r.action, r.actor_email, r.target_type, r.target_key, r.detail]
      .some((v) => (v || '').toString().toLowerCase().includes(q));
  });

  const inner = (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          {!embedded && <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>}
          <p className="text-sm text-gray-500">Privileged admin actions (read-only)</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          />
          <button onClick={load} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded">Refresh</button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading…</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600 sticky top-0">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Admin</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Target</th>
                  <th className="px-3 py-2">Detail</th>
                  <th className="px-3 py-2">IP</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">{fmt(r.created_at)}</td>
                    <td className="px-3 py-2">{r.actor_email || r.actor_id || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${ACTION_STYLE[r.action] || 'bg-gray-100 text-gray-600'}`}>
                        {r.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {r.target_type ? `${r.target_type}: ${r.target_key || ''}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-500 max-w-xs truncate" title={r.detail || ''}>{r.detail || '—'}</td>
                    <td className="px-3 py-2 text-gray-400">{r.ip || '—'}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">No audit entries.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );

  if (embedded) return inner;
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">{inner}</div>
    </div>
  );
};

export default AdminAuditLog;
